/**
 * Public, customer-shareable Compass demo at /compass/demo.
 *
 * Uses the real Compass roadmap (PHASES/TEMPLATES), readiness rules
 * (progressRules) and pricing math (computePricingWorkbook), but stores
 * everything under its own local-only key (demoStore). It never uses auth,
 * the API, or the real draft store, and grants no access to anything.
 */
import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Compass as CompassIcon, Menu, X, Check, Lock, Eye, RotateCcw, AlertTriangle,
  ArrowRight, Save, Plus, Trash2,
} from "lucide-react";
import { PHASES, TEMPLATES, computePricingWorkbook } from "@/compass/data";
import {
  phaseReadiness, currentPhaseNumber, completePhase, reopenPhase, effectiveCompleted,
  templateHasRequiredInformation, hasMeaningfulDraft, reconfirmAfterWorksheetEdit,
} from "@/compass/progressRules";
import {
  loadDemoState, saveDemoState, resetDemoState, storageAvailable, DEMO_INTERACTIVE_PHASES,
} from "@/compass/demoStore";
import CopyDemoLinkButton from "@/components/CopyDemoLinkButton";
import { PlanningDetails } from "@/compass/PlanningDetails";
import { reviewIssues, detailExportRows, PLANNING_DETAILS, detailTables } from "@/compass/planningDetails";

const PLANS_HREF = "/compass#compass-pricing";

function missingRequired(template, values) {
  if (!template) return [];
  const v = values || {};
  if (Array.isArray(template.sections)) {
    return template.sections
      .filter((s) => (s.fields || s.table) && !templateHasRequiredInformation({ sections: [s] }, v))
      .map((s) => s.title);
  }
  return [...template.fields.filter((f) => !hasMeaningfulDraft(v[f])), ...reviewIssues(v)];
}

function downloadDemoWorksheet(template, values) {
  const rows = [[`${template.name} — fictional Compass demo; not validated or approved`]];
  if (template.sections) {
    const calculated = template.compute(values);
    template.sections.forEach((s) => {
      rows.push([s.title]);
      (s.fields || []).forEach((f) => rows.push([f.label, values[f.id] ?? ""]));
      if (s.table) {
        rows.push(s.table.columns.map((c) => c.label));
        (values[s.table.id] || []).forEach((r) => rows.push(s.table.columns.map((c) => r[c.id] ?? "")));
      }
      (s.results || []).forEach((r) => rows.push([r.label, fmt(calculated[r.key], r.format)]));
    });
  } else template.fields.forEach((f) => rows.push([f, values[f] || ""]));
  rows.push(...detailExportRows(template.id, values));
  const url = URL.createObjectURL(new Blob([rows.map((r) => r.join(" | ")).join("\n")], { type: "text/plain" }));
  const a = document.createElement("a");
  a.href = url; a.download = `${template.id}-fictional-demo.txt`; a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function fmt(value, format) {
  if (format === "text") return value;
  if (!Number.isFinite(value)) return "-";
  if (format === "money") return `${value < 0 ? "-" : ""}$${Math.abs(value).toFixed(2)}`;
  if (format === "percent") return `${(value * 100).toFixed(1)}%`;
  return (Math.round(value * 10) / 10).toLocaleString();
}

function SimpleWorksheet({ template, values, onChange }) {
  return (
    <div className="space-y-4">
      {template.fields.map((field, i) => (
        <label key={field} className="block">
          <span className="text-sm font-medium text-stone-800">{field}</span>
          <textarea
            rows={2}
            value={values[field] || ""}
            onChange={(e) => onChange({ ...values, [field]: e.target.value })}
            className="mt-1 w-full rounded-lg border border-stone-300 bg-white px-3 py-2 text-sm focus:border-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-700/20"
            data-testid={`input-${template.id}-${i}`}
          />
        </label>
      ))}
    </div>
  );
}

function PricingWorksheet({ template, values, onChange }) {
  const calc = useMemo(() => computePricingWorkbook(values), [values]);
  const setRow = (tableId, idx, col, val) => {
    const rows = [...(values[tableId] || [])];
    rows[idx] = { ...rows[idx], [col]: val };
    onChange({ ...values, [tableId]: rows });
  };
  return (
    <div className="space-y-6">
      <div className="grid gap-3 rounded-xl bg-emerald-900 p-4 text-emerald-50 sm:grid-cols-3" data-testid="panel-pricing-summary">
        <div><p className="text-xs uppercase tracking-wider text-emerald-200">Manufacturing COGS / unit</p><p className="font-mono text-2xl" data-testid="text-true-cost">{fmt(calc.trueCost, "money")}</p></div>
        <div><p className="text-xs uppercase tracking-wider text-emerald-200">Target price</p><p className="font-mono text-2xl" data-testid="text-target-price">{fmt(calc.targetPrice, "money")}</p></div>
        <div><p className="text-xs uppercase tracking-wider text-emerald-200">Break-even / month</p><p className="font-mono text-2xl" data-testid="text-break-even">{fmt(calc.breakEvenUnits, "number")} units</p></div>
      </div>
      {template.sections.map((section) => (
        <section key={section.id} className="rounded-xl border border-stone-200 bg-white p-4">
          <h4 className="font-semibold text-stone-900">{section.title}</h4>
          {section.intro && <p className="mt-1 text-xs text-stone-500">{section.intro}</p>}
          {section.fields && (
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              {section.fields.map((f) => (
                <label key={f.id} className="block text-sm">
                  <span className="text-stone-700">{f.label}{f.unit ? <span className="text-stone-400"> ({f.unit})</span> : null}</span>
                  <input
                    type="text"
                    inputMode={f.type === "text" ? "text" : "decimal"}
                    value={values[f.id] || ""}
                    onChange={(e) => onChange({ ...values, [f.id]: e.target.value })}
                    className="mt-1 w-full rounded-lg border border-stone-300 px-3 py-2 font-mono text-sm focus:border-emerald-700 focus:outline-none"
                    data-testid={`input-${f.id}`}
                  />
                </label>
              ))}
            </div>
          )}
          {section.table && (
            <div className="mt-3 overflow-x-auto">
              <table className="w-full min-w-[520px] text-sm">
                <thead><tr>{section.table.columns.map((c) => <th key={c.id} className="px-1 pb-1 text-left text-xs font-medium text-stone-500">{c.label}</th>)}<th /></tr></thead>
                <tbody>
                  {(values[section.table.id] || []).map((row, idx) => (
                    <tr key={idx}>
                      {section.table.columns.map((c) => (
                        <td key={c.id} className="p-1">
                          <input
                            value={row[c.id] || ""}
                            onChange={(e) => setRow(section.table.id, idx, c.id, e.target.value)}
                            className="w-full rounded border border-stone-300 px-2 py-1 font-mono text-xs"
                            aria-label={`${c.label} row ${idx + 1}`}
                            data-testid={`input-${section.table.id}-${idx}-${c.id}`}
                          />
                        </td>
                      ))}
                      <td className="p-1">
                        <button type="button" aria-label="Remove row" onClick={() => onChange({ ...values, [section.table.id]: (values[section.table.id] || []).filter((_, i) => i !== idx) })} className="rounded p-1 text-stone-400 hover:text-red-700" data-testid={`button-remove-${section.table.id}-${idx}`}><Trash2 className="h-4 w-4" /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <button type="button" onClick={() => onChange({ ...values, [section.table.id]: [...(values[section.table.id] || []), {}] })} className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-emerald-800" data-testid={`button-add-${section.table.id}`}><Plus className="h-3 w-3" />{section.table.addLabel || "Add row"}</button>
            </div>
          )}
          {section.results && (
            <dl className="mt-3 divide-y divide-stone-100 rounded-lg bg-stone-50 px-3">
              {section.results.map((r) => (
                <div key={r.key + section.id} className="flex items-baseline justify-between gap-3 py-1.5 text-sm">
                  <dt className={r.strong ? "font-semibold text-stone-900" : "text-stone-600"}>{r.label}</dt>
                  <dd className={`font-mono ${r.strong ? "font-semibold text-emerald-800" : "text-stone-800"}`}>{fmt(calc[r.key], r.format)}</dd>
                </div>
              ))}
            </dl>
          )}
        </section>
      ))}
    </div>
  );
}

function WorksheetCard({ templateId, saved, onSave }) {
  const template = TEMPLATES.find((t) => t.id === templateId);
  const [values, setValues] = useState(saved || {});
  const [message, setMessage] = useState(null);
  const dirty = JSON.stringify(values) !== JSON.stringify(saved || {});
  if (!template) return null;
  const complete = templateHasRequiredInformation(template, saved);

  const save = () => {
    onSave(templateId, values);
    const missing = missingRequired(template, values);
    setMessage(missing.length
      ? { tone: "warn", text: `Saved. Still needed before this worksheet counts toward readiness: ${missing.join("; ")}.` }
      : { tone: "ok", text: "Saved. All required information is present." });
  };

  return (
    <article className="rounded-2xl border border-stone-200 bg-stone-50/60 p-4 sm:p-5" data-testid={`worksheet-${templateId}`}>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <h3 className="font-semibold text-stone-900">{template.name}</h3>
        <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${complete ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`} data-testid={`status-worksheet-${templateId}`}>
          {complete ? "Required info saved" : "Incomplete"}
        </span>
      </div>
      {Array.isArray(template.sections)
        ? <PricingWorksheet template={template} values={values} onChange={setValues} />
        : <SimpleWorksheet template={template} values={values} onChange={setValues} />}
      <PlanningDetails templateId={templateId} values={values} onChange={setValues} />
      <div className="mt-4 flex flex-wrap items-center gap-3">
        <button type="button" onClick={save} disabled={!dirty} className="inline-flex items-center gap-2 rounded-lg bg-emerald-800 px-4 py-2 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:bg-stone-300" data-testid={`button-save-${templateId}`}>
          <Save className="h-4 w-4" /> Save worksheet
        </button>
        <button type="button" onClick={() => downloadDemoWorksheet(template, values)} className="rounded-lg border border-emerald-800 px-4 py-2 text-sm text-emerald-900">Download demo worksheet</button>
        {dirty && <span className="text-xs text-amber-800">Unsaved changes</span>}
      </div>
      {message && (
        <p role="status" className={`mt-3 rounded-lg px-3 py-2 text-sm ${message.tone === "ok" ? "bg-emerald-50 text-emerald-900" : "bg-amber-50 text-amber-900"}`} data-testid={`message-save-${templateId}`}>{message.text}</p>
      )}
    </article>
  );
}

function ReadinessPanel({ phase, progress, drafts, onProgress }) {
  const r = phaseReadiness(progress, phase, drafts);
  const checked = new Set(progress.checklists?.[phase.n] || []);
  const record = progress.phaseRecords?.[phase.n] || {};
  const setRecord = (patch) => onProgress((p) => ({ ...p, phaseRecords: { ...p.phaseRecords, [phase.n]: { ...record, ...patch } } }));
  const toggle = (i) => onProgress((p) => {
    const next = new Set(p.checklists?.[phase.n] || []);
    next.has(i) ? next.delete(i) : next.add(i);
    return { ...p, checklists: { ...p.checklists, [phase.n]: [...next].sort((a, b) => a - b) } };
  });
  const items = [
    ["Checklist complete", r.checklistReady],
    ["Worksheets saved with required info, or evidence recorded", r.templatesReady || r.evidenceReady],
    ["Open questions have a named owner, or none remain", r.questionsReady],
  ];
  return (
    <div className="space-y-5">
      <div>
        <h3 className="mb-2 text-sm font-semibold uppercase tracking-wider text-stone-500">Stage checklist</h3>
        <ul className="space-y-2">
          {phase.checklist.map((item, i) => (
            <li key={item}>
              <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-stone-200 bg-white p-3 text-sm hover:border-emerald-700">
                <input type="checkbox" checked={checked.has(i)} onChange={() => toggle(i)} className="mt-0.5 h-4 w-4 accent-emerald-800" data-testid={`checkbox-phase-${phase.n}-${i}`} />
                <span>{item}</span>
              </label>
            </li>
          ))}
        </ul>
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <label className="block text-sm">
          <span className="font-medium text-stone-800">Open questions</span>
          <textarea rows={2} value={record.questionsDetails || ""} disabled={!!record.noUnresolvedQuestions} onChange={(e) => setRecord({ questionsDetails: e.target.value })} className="mt-1 w-full rounded-lg border border-stone-300 px-3 py-2 disabled:bg-stone-100" data-testid={`input-questions-${phase.n}`} />
        </label>
        <label className="block text-sm">
          <span className="font-medium text-stone-800">Who owns them</span>
          <input value={record.questionsOwner || ""} disabled={!!record.noUnresolvedQuestions} onChange={(e) => setRecord({ questionsOwner: e.target.value })} className="mt-1 w-full rounded-lg border border-stone-300 px-3 py-2 disabled:bg-stone-100" data-testid={`input-questions-owner-${phase.n}`} />
          <span className="mt-2 flex items-center gap-2 text-stone-600">
            <input type="checkbox" checked={!!record.noUnresolvedQuestions} onChange={(e) => setRecord({ noUnresolvedQuestions: e.target.checked })} className="accent-emerald-800" data-testid={`checkbox-no-questions-${phase.n}`} />
            No unresolved questions
          </span>
        </label>
      </div>
      <ul className="space-y-1.5 rounded-xl border border-stone-200 bg-white p-4" data-testid={`readiness-${phase.n}`}>
        {items.map(([label, ok]) => (
          <li key={label} className="flex items-center gap-2 text-sm">
            <span className={`grid h-5 w-5 place-items-center rounded-full ${ok ? "bg-emerald-700 text-white" : "border border-stone-300 text-transparent"}`}><Check className="h-3 w-3" /></span>
            <span className={ok ? "text-stone-900" : "text-stone-500"}>{label}</span>
          </li>
        ))}
      </ul>
      <p className="text-xs text-stone-500">Readiness here is self-reported planning progress, not regulatory, safety, or professional approval.</p>
      <button
        type="button"
        disabled={!r.ready}
        onClick={() => onProgress((p) => completePhase(p, phase.n))}
        className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-emerald-800 px-5 py-3 font-semibold text-white disabled:cursor-not-allowed disabled:bg-stone-300 sm:w-auto"
        data-testid={`button-complete-phase-${phase.n}`}
      >
        Mark stage {phase.n} ready and continue <ArrowRight className="h-4 w-4" />
      </button>
      {!r.ready && <p className="text-xs text-stone-500" data-testid={`text-not-ready-${phase.n}`}>Finish the unchecked items above to continue.</p>}
    </div>
  );
}

export default function CompassDemo() {
  const [state, setState] = useState(() => loadDemoState());
  const [persistent] = useState(() => storageAvailable());
  const [selected, setSelected] = useState(() => currentPhaseNumber(loadDemoState().progress));
  const [menuOpen, setMenuOpen] = useState(false);
  const [confirmReset, setConfirmReset] = useState(false);
  const [notice, setNotice] = useState("");
  const [resetKey, setResetKey] = useState(0);

  const { progress, drafts } = state;
  const current = currentPhaseNumber(progress);
  const done = new Set(effectiveCompleted(progress));
  const phase = PHASES.find((p) => p.n === selected) || PHASES[0];
  const interactive = DEMO_INTERACTIVE_PHASES.includes(phase.n);
  const isLocked = interactive && phase.n > current;
  const scopeFinished = DEMO_INTERACTIVE_PHASES.every((n) => done.has(n));

  const commit = (next, msg) => {
    const withTime = { ...next, savedAt: new Date().toISOString() };
    setState(withTime);
    const res = saveDemoState(withTime);
    setNotice(res.ok ? (msg || "Saved in this browser.") : "Not saved: browser storage is unavailable. Changes last until you leave this page.");
  };
  const onProgress = (updater) => {
    const before = currentPhaseNumber(state.progress);
    const nextProgress = updater(state.progress);
    commit({ ...state, progress: nextProgress });
    const after = currentPhaseNumber(nextProgress);
    if (after !== before && effectiveCompleted(nextProgress).includes(before)) {
      setSelected(after);
      setNotice(`Stage ${before} marked ready. Saved in this browser.`);
    }
  };
  const onSaveWorksheet = (id, values) => {
    const progress = reconfirmAfterWorksheetEdit(state.progress, id, state.drafts[id], values);
    commit({ ...state, progress, drafts: { ...state.drafts, [id]: values } },
      progress !== state.progress ? "Worksheet saved. Affected completed stages now need reconfirmation; all saved work is retained." : undefined);
  };
  const doReset = () => {
    const seed = resetDemoState();
    setState(seed);
    setSelected(currentPhaseNumber(seed.progress));
    setResetKey((k) => k + 1);
    setConfirmReset(false);
    setNotice("Demo restored to the original sample. Only demo data was affected.");
  };
  const reopen = (n) => {
    if (!window.confirm(`Reopen stage ${n}? Later stages will need to be reconfirmed. Saved worksheets are kept.`)) return;
    onProgress((p) => reopenPhase(p, n));
    setSelected(n);
  };

  const StageNav = () => (
    <nav aria-label="Compass stages" className="space-y-1">
      {PHASES.map((p) => {
        const isDone = done.has(p.n);
        const preview = !DEMO_INTERACTIVE_PHASES.includes(p.n);
        return (
          <button
            key={p.n}
            type="button"
            onClick={() => { setSelected(p.n); setMenuOpen(false); }}
            className={`flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left text-sm transition-colors ${selected === p.n ? "bg-emerald-900 text-white" : "text-stone-700 hover:bg-stone-100"}`}
            data-testid={`nav-stage-${p.n}`}
          >
            <span className={`grid h-6 w-6 shrink-0 place-items-center rounded-full text-xs font-bold ${isDone ? "bg-emerald-600 text-white" : p.n === current ? "bg-amber-500 text-stone-900" : "border border-stone-300"}`}>
              {isDone ? <Check className="h-3.5 w-3.5" /> : p.n}
            </span>
            <span className="flex-1">{p.name}</span>
            {preview && <Eye className="h-3.5 w-3.5 opacity-60" aria-label="Preview only" />}
          </button>
        );
      })}
    </nav>
  );

  return (
    <div className="platform-blue min-h-[100dvh] bg-[#f6f3ec] text-stone-900" data-testid="compass-demo">
      <div className="bg-amber-100 px-4 py-2.5 text-center text-sm text-amber-950" role="note" data-testid="banner-demo-disclaimer">
        <strong>Interactive sample demo.</strong> Fictional example data, saved only in this browser. Nothing is sent to Alaine & Co. Do not enter confidential recipes or business information.
      </div>
      <header className="sticky top-0 z-30 border-b border-stone-200 bg-[#f6f3ec]/95 backdrop-blur">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-3 px-4 py-3">
          <div className="flex items-center gap-2">
            <button type="button" className="rounded-lg p-2 lg:hidden" onClick={() => setMenuOpen((o) => !o)} aria-label={menuOpen ? "Close stage menu" : "Open stage menu"} aria-expanded={menuOpen} data-testid="button-mobile-menu">
              {menuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
            <CompassIcon className="h-6 w-6 text-emerald-800" />
            <div className="leading-tight">
              <p className="font-bold">Alaine's Compass <span className="ml-1 rounded bg-emerald-900 px-1.5 py-0.5 align-middle text-[10px] font-semibold uppercase tracking-wider text-emerald-50">Demo</span></p>
              <p className="text-xs text-stone-500">by Alaine & Co.</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <CopyDemoLinkButton className="hidden sm:block" />
            <Link to={PLANS_HREF} className="rounded-lg bg-emerald-800 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-900" data-testid="link-get-compass">Get Compass</Link>
          </div>
        </div>
        {menuOpen && (
          <div className="border-t border-stone-200 bg-[#f6f3ec] px-4 py-3 lg:hidden" data-testid="menu-mobile">
            <StageNav />
            <CopyDemoLinkButton className="mt-3" />
          </div>
        )}
      </header>

      <div className="mx-auto grid max-w-7xl gap-8 px-4 py-8 lg:grid-cols-[260px_1fr]">
        <aside className="hidden lg:block">
          <div className="sticky top-24 space-y-6">
            <StageNav />
            <div className="rounded-xl border border-stone-200 bg-white p-4 text-xs text-stone-600">
              <p className="mb-1 font-semibold text-stone-800">Demo scope</p>
              Stages 1-3 are editable. Stages 4-8 show a read-only preview of the guidance in full Compass.
            </div>
          </div>
        </aside>

        <main className="min-w-0 space-y-8">
          {state.sampleUpdateNotice && <p role="status" className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-950">{state.sampleUpdateNotice}</p>}
          <section className="rounded-2xl border border-stone-200 bg-white p-5 sm:p-6" data-testid="section-start-here">
            <p className="text-xs font-semibold uppercase tracking-widest text-amber-700">Start here</p>
            <h1 className="mt-1 text-2xl font-bold sm:text-3xl">{progress.productName || "Sample product"}</h1>
            <ol className="mt-3 grid gap-2 text-sm text-stone-700 sm:grid-cols-2">
              <li><strong>1.</strong> Stage 1 is already marked ready so you can see a finished stage.</li>
              <li><strong>2.</strong> In stage 2, fill the two empty worksheet fields and save.</li>
              <li><strong>3.</strong> Tick the remaining checklist items, then mark the stage ready.</li>
              <li><strong>4.</strong> In stage 3, change any costing number and watch the pricing update.</li>
            </ol>
            <p className="mt-3 text-xs text-stone-500">The ranch dressing is a fictional planning example, not a validated recipe, process, or safety specification.</p>
            <div className="mt-4 flex flex-wrap items-center gap-3 border-t border-stone-100 pt-4">
              <span className="text-xs text-stone-600" data-testid="text-persistence">
                {persistent
                  ? "Your demo edits are kept in this browser only and survive a reload. Clearing site data removes them."
                  : "Browser storage is unavailable, so demo edits last only until you leave or reload this page."}
              </span>
              {!confirmReset ? (
                <button type="button" onClick={() => setConfirmReset(true)} className="inline-flex items-center gap-1.5 rounded-lg border border-stone-300 px-3 py-1.5 text-xs font-semibold text-stone-700 hover:border-red-700 hover:text-red-800" data-testid="button-reset-demo">
                  <RotateCcw className="h-3.5 w-3.5" /> Reset demo
                </button>
              ) : (
                <span className="inline-flex flex-wrap items-center gap-2 rounded-lg bg-red-50 px-3 py-1.5 text-xs text-red-900" role="alertdialog" aria-label="Confirm reset">
                  <AlertTriangle className="h-3.5 w-3.5" /> Restore the original sample and discard your demo edits?
                  <button type="button" onClick={doReset} className="rounded bg-red-800 px-2 py-1 font-semibold text-white" data-testid="button-confirm-reset">Reset</button>
                  <button type="button" onClick={() => setConfirmReset(false)} className="rounded px-2 py-1 font-semibold" data-testid="button-cancel-reset">Cancel</button>
                </span>
              )}
            </div>
            {notice && <p role="status" className="mt-3 text-sm font-medium text-emerald-900" data-testid="text-demo-notice">{notice}</p>}
          </section>

          <section key={`${phase.n}-${resetKey}`} className="space-y-6" data-testid={`stage-${phase.n}`}>
            <div>
              <p className="text-xs font-semibold uppercase tracking-widest text-stone-500">
                Stage {phase.n} of {PHASES.length}
                {done.has(phase.n) && <span className="ml-2 text-emerald-700">Ready</span>}
                {!interactive && <span className="ml-2 text-amber-700">Preview only in this demo</span>}
              </p>
              <h2 className="mt-1 text-3xl font-bold tracking-tight">{phase.name}</h2>
              <p className="mt-1 text-stone-600">{phase.desc}</p>
            </div>
            <div className="grid gap-3 md:grid-cols-3">
              {[["What", phase.what], ["Why it matters", phase.why], ["Have ready", phase.preparation]].map(([t, body]) => (
                <div key={t} className="rounded-xl border border-stone-200 bg-white p-4">
                  <p className="text-xs font-semibold uppercase tracking-wider text-amber-700">{t}</p>
                  <p className="mt-1 text-sm text-stone-700">{body}</p>
                </div>
              ))}
            </div>

            {!interactive ? (
              <div className="rounded-2xl border border-dashed border-stone-300 bg-white p-5" data-testid={`preview-${phase.n}`}>
                <p className="flex items-center gap-2 font-semibold"><Eye className="h-4 w-4 text-amber-700" /> Preview of this stage</p>
                <ul className="mt-3 space-y-1.5 text-sm text-stone-700">
                  {phase.checklist.map((c) => <li key={c} className="flex gap-2"><span className="text-stone-400">-</span>{c}</li>)}
                </ul>
                <p className="mt-4 text-sm text-stone-600">Worksheets in full Compass: {phase.templates.map((id) => TEMPLATES.find((t) => t.id === id)?.name).filter(Boolean).join(", ")}.</p>
                <p className="mt-2 text-xs text-stone-500">Editing for this stage is not part of the demo.</p>
                {phase.templates.map((id) => {
                  const t = TEMPLATES.find((item) => item.id === id);
                  if (!t) return null;
                  return <details key={id} className="mt-4 rounded-lg border border-stone-200 p-4">
                    <summary className="cursor-pointer font-semibold text-emerald-900">View {t.name} worksheet contents</summary>
                    <p className="mt-2 text-xs text-stone-500">Read-only preview. In full Compass these fields can be filled, saved and exported.</p>
                    <ul className="mt-3 list-disc space-y-2 pl-5 text-sm">
                      {(t.fields || []).map((f) => <li key={f}>{f}</li>)}
                      {(PLANNING_DETAILS[id]?.fields || []).map((f) => <li key={f.id}>{f.label}</li>)}
                    </ul>
                    {detailTables(id).map((table) => <div key={table.id} className="mt-4 text-sm">
                      <h4 className="font-semibold">{table.title}</h4>
                      <p className="mt-1 text-stone-600">{table.columns.map((c) => c.label).join(" · ")}</p>
                    </div>)}
                    <p className="mt-3 text-xs text-stone-500">Review records are self-reported evidence, not verified professional approval.</p>
                  </details>;
                })}
              </div>
            ) : isLocked ? (
              <div className="rounded-2xl border border-stone-200 bg-white p-5" data-testid={`locked-${phase.n}`}>
                <p className="flex items-center gap-2 font-semibold"><Lock className="h-4 w-4" /> Finish stage {current} first</p>
                <p className="mt-1 text-sm text-stone-600">Compass moves one stage at a time so later decisions rest on earlier work.</p>
                <button type="button" onClick={() => setSelected(current)} className="mt-3 text-sm font-semibold text-emerald-800" data-testid="button-go-current">Go to stage {current}</button>
              </div>
            ) : (
              <>
                <div className="space-y-5">
                  {phase.templates.map((id) => (
                    <WorksheetCard key={id} templateId={id} saved={drafts[id]} onSave={onSaveWorksheet} />
                  ))}
                </div>
                {done.has(phase.n) ? (
                  <div className="flex flex-wrap items-center gap-3 rounded-xl bg-emerald-50 p-4 text-sm text-emerald-900">
                    <Check className="h-4 w-4" /> This stage is marked ready.
                    <button type="button" onClick={() => reopen(phase.n)} className="font-semibold underline" data-testid={`button-reopen-${phase.n}`}>Reopen stage</button>
                  </div>
                ) : (
                  <ReadinessPanel phase={phase} progress={progress} drafts={drafts} onProgress={onProgress} />
                )}
              </>
            )}
          </section>

          {scopeFinished && (
            <section className="rounded-2xl bg-emerald-900 p-6 text-emerald-50" data-testid="section-demo-complete">
              <h2 className="text-xl font-bold">You have reached the end of the demo scope.</h2>
              <p className="mt-1 text-sm text-emerald-100">Full Compass continues with testing, packaging, co-manufacturing, regulatory, and launch readiness, saved to your own account.</p>
              <Link to={PLANS_HREF} className="mt-4 inline-flex items-center gap-2 rounded-lg bg-amber-400 px-4 py-2 text-sm font-semibold text-stone-900" data-testid="link-get-compass-end">See Compass plans <ArrowRight className="h-4 w-4" /></Link>
            </section>
          )}
        </main>
      </div>
      <footer className="border-t border-stone-200 py-6 text-center text-xs text-stone-500">
        Alaine's Compass by Alaine & Co. · Sample demo, not professional or regulatory advice. ·{" "}
        <Link to="/terms" className="hover:text-emerald-800">Terms</Link> · <Link to="/privacy" className="hover:text-emerald-800">Privacy</Link>
      </footer>
    </div>
  );
}
